import React, { useRef } from 'react';
import { useDispatch } from 'react-redux';
import { sendGAEvent } from '@noths/polaris-client-google-analytics';
import { LazyRender } from '@noths/polaris-client-ribbons-design-system';
import dynamic from 'next/dynamic';

import { BasketErrorNotification } from 'src/components/atoms/BasketErrorNotification/BasketErrorNotification';
import {
  ABOUT_THE_PRODUCT_HEADING,
  PRODUCT_DETAILS_HEADING,
} from 'src/components/layouts/PDP/copy';
import { PARTNER_MODULE_ID } from 'src/components/layouts/PDP/elementIds';
import { BasketCTAButtonContainer } from 'src/components/molecules/BasketCTAButton/BasketCTAButtonContainer';
import { MAIN_PAGE_CTA_ID } from 'src/components/molecules/BasketCTAButton/elementIDs';
import { trackOpenContactPartnerModal } from 'src/components/molecules/ContactPartnerModal/tracking/contactPartnerModalEvents';
import { DeliverySummary } from 'src/components/molecules/DeliverySummary/DeliverySummary';
import { ExpandablePanel } from 'src/components/molecules/ExpandablePanel/ExpandablePanel';
import { MembershipBannerSection } from 'src/components/molecules/MembershipBannerSection/MembershipBannerSection';
import { PartnerMaxiModuleWithTracking } from 'src/components/molecules/PartnerMaxiModule/components/PartnerMaxiModuleWithTracking';
import { ProductRatingMaxiSummary } from 'src/components/molecules/ProductRatingMaxiSummary/ProductRatingMaxiSummary';
import { PublicAttributes } from 'src/components/molecules/PublicAttributes/PublicAttributes';
import { StickySection } from 'src/components/molecules/StickySection/components/StickySection';
import { TrustCarouselWithTracking } from 'src/components/molecules/TrustCarousel/components/TrustCarouselWithTracking';
import { LinkCarouselContainer } from 'src/components/organisms/LinkCarousel/containers/LinkCarouselContainer';
import { ProductDescriptionContainer } from 'src/components/organisms/ProductDescription/containers/ProductDescriptionContainer';
import { ProductImagesCarouselContainer } from 'src/components/organisms/ProductImagesCarousel/ProductImagesCarouselContainer';
import { ProductImagesModalContainer } from 'src/components/organisms/ProductImagesModal/ProductImagesModalContainer';
import { ProductOptionsContainer } from 'src/components/organisms/ProductOptions/ProductOptionsContainer';
import { ProductReviewsContainer } from 'src/components/organisms/ProductReviews/containers/ProductReviewsContainer';
import { RecommendedProductsCarouselContainer } from 'src/components/organisms/RecommendedProductsCarousel/containers/RecommendedProductsCarouselContainer';
import { REVIEWS_URL_FRAGMENT_ID } from 'src/fragmentIDs';
import { actions } from 'src/redux/contactPartner/slice';
import { trackBreadcrumbClick } from 'src/tracking/trackBreadcrumbClick';
import { useEditProductOptions } from '../hooks/useEditProductOptions';
import { PDPLayoutView } from './PDPLayoutView';
import { usePDPViewModel } from './usePDPViewModel';
import * as styles from './PDPLayoutView.styles';

const DeliveryDrawer = dynamic(() =>
  import(
    /* webpackChunkName: "delivery_drawer" */ 'src/components/organisms/DeliveryDrawer/DeliveryDrawer'
  ).then((mod) => mod.DeliveryDrawer),
);

const ProductOptionsDrawerContainer = dynamic(() =>
  import(
    /* webpackChunkName: "product_options_drawer" */ 'src/components/organisms/ProductOptions/ProductOptionsDrawerContainer'
  ).then((mod) => mod.ProductOptionsDrawerContainer),
);

const RECOMMENDED_PRODUCTS_LAZY_LOAD_MARGIN = '200px';

export const PDPLayout = () => {
  const dispatch = useDispatch();
  const {
    deliveryOptionsLoaded,
    hasProductOptions,
    partnerMaxi,
    publicAttributes,
    similarProductsServingConfig,
    viewModel,
  } = usePDPViewModel();
  const rightColumnRef = useRef<HTMLDivElement>(null);

  useEditProductOptions();

  const openContactPartnerModal = () => {
    dispatch(actions.openContactPartnerModal());
    trackOpenContactPartnerModal(viewModel.partner.name);
  };

  return (
    <>
      <PDPLayoutView
        ctaColumnRef={rightColumnRef}
        ctaTrigger={
          <>
            {deliveryOptionsLoaded && <DeliverySummary />}
            <ProductOptionsContainer />
            <BasketCTAButtonContainer />
          </>
        }
        description={
          <ExpandablePanel
            collapseButtonText="Read less"
            expandButtonText="Read more"
            onToggle={(expanded) =>
              sendGAEvent({
                event: 'custom_event',
                event_category: ABOUT_THE_PRODUCT_HEADING,
                event_action: expanded ? 'Expand' : 'Collapse',
                event_label: 'not set',
              })
            }
          >
            <h2 css={styles.heading}>{ABOUT_THE_PRODUCT_HEADING}</h2>
            <ProductDescriptionContainer />
          </ExpandablePanel>
        }
        images={<ProductImagesCarouselContainer />}
        linkCarousel={<LinkCarouselContainer />}
        membershipBanner={<MembershipBannerSection />}
        onBreadcrumbLinkClick={trackBreadcrumbClick}
        partnerModules={
          <div id={PARTNER_MODULE_ID}>
            <PartnerMaxiModuleWithTracking
              location={viewModel.partner.location}
              logoSrc={viewModel.partner.logoSrc}
              name={viewModel.partner.name}
              onContactPartnerClick={openContactPartnerModal}
              partnerLinkHref={partnerMaxi.linkHref}
              partnerRating={viewModel.partnerReviews.rating}
              partnerReviewCount={viewModel.partnerReviews.count}
              partnerShortcode={partnerMaxi.shortcode}
            />
            <div css={styles.partnerProductsCarousel}>
              <LazyRender options={{ rootMargin: RECOMMENDED_PRODUCTS_LAZY_LOAD_MARGIN }}>
                <RecommendedProductsCarouselContainer
                  servingConfig="pdp_same_seller_2"
                  title="Our bestsellers"
                />
              </LazyRender>
            </div>
          </div>
        }
        publicAttributes={
          publicAttributes && publicAttributes.length > 0 ? (
            <ExpandablePanel
              collapseButtonText="Read less"
              expandButtonText="Read more"
              onToggle={(expanded) =>
                sendGAEvent({
                  event: 'custom_event',
                  event_category: PRODUCT_DETAILS_HEADING,
                  event_action: expanded ? 'Expand' : 'Collapse',
                  event_label: 'not set',
                })
              }
            >
              <h2 css={styles.heading}>{PRODUCT_DETAILS_HEADING}</h2>
              <PublicAttributes publicAttributes={publicAttributes} />
            </ExpandablePanel>
          ) : undefined
        }
        recommendedProducts={
          <LazyRender options={{ rootMargin: RECOMMENDED_PRODUCTS_LAZY_LOAD_MARGIN }}>
            <RecommendedProductsCarouselContainer
              servingConfig={similarProductsServingConfig}
              title="Similar products"
            />
          </LazyRender>
        }
        reviews={
          <div id={REVIEWS_URL_FRAGMENT_ID}>
            <div css={styles.overallRatingWrapper}>
              <ProductRatingMaxiSummary
                averageRating={viewModel.productReviews.rating}
                numberOfReviews={viewModel.productReviews.count}
              />
            </div>
            <div css={styles.productReviewsWrapper}>
              <ProductReviewsContainer />
            </div>
          </div>
        }
        stickyCta={
          <StickySection triggerElementId={MAIN_PAGE_CTA_ID}>
            <BasketCTAButtonContainer />
          </StickySection>
        }
        trustCarousel={<TrustCarouselWithTracking />}
        viewModel={viewModel}
      />
      <ProductImagesModalContainer />
      <DeliveryDrawer target={rightColumnRef} />
      {hasProductOptions && <ProductOptionsDrawerContainer target={rightColumnRef} />}
      <BasketErrorNotification />
    </>
  );
};
